import { forwardRef, useImperativeHandle, useEffect } from "react";
import { Box, Space, Text, Title } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { Product, Order, RemovableItem } from "../../../types/interfaces";
import { UPDATE_ORDER } from "../../../gql/orders";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import {
  Rdv2ChoiceType,
  Rdv2ReturnMethod,
  RemovableItemOrderType,
  ImprintType,
  ItemType,
} from "../../../types/enums";
import { ButtonsSelect } from "../../button/ButtonsSelect";

type PartialDentureRdv2ChoiceFormData = {
  rdv2Choice: Rdv2ChoiceType | null;
  returnMethod: Rdv2ReturnMethod | null;
};

type PartialDentureRdv2ChoiceProps = {
  product?: Product;
  previousProduct?: Product;
  isEditMode?: boolean;
  order?: Order;
};

export interface PartialDentureRdv2ChoiceRef {
  saveData: () => Promise<void>;
}

const rdv2ChoiceOptionsAfterWaxBite = [
  {
    value: Rdv2ChoiceType.CONTINUE_TO_FINISH,
    label: "Poursuivre vers la finition",
  },
  {
    value: Rdv2ChoiceType.REQUEST_TRY_IN,
    label: "Demander un essayage",
  },
  {
    value: Rdv2ChoiceType.REQUEST_NEW_WAX_BITE,
    label: "Demander une nouvelle cire d'occlusion",
  },
];

const rdv2ChoiceOptionsAfterTryin = [
  {
    value: Rdv2ChoiceType.CONTINUE_TO_FINISH,
    label: "Poursuivre vers la finition",
  },
  {
    value: Rdv2ChoiceType.REQUEST_TRY_IN,
    label: "Demander un nouvel essayage",
  },
];

const returnMethodOptionsWaxBite = [
  {
    value: Rdv2ReturnMethod.PICKUP,
    label:
      "Je demande un ramassage de mes cires d'occlusion par transporteur et les retourne à Jasper",
  },
  {
    value: Rdv2ReturnMethod.DIGITAL_IMPRINT,
    label:
      "Je prends une empreinte numérique de mes cires d'occlusion en bouche et ne les renvoie pas à Jasper",
  },
];

// build the fragment that copies teethToManufacture and the partialDenture relation
// (missingTeeth, hooks, metal bands, custom tray) from the partialDenture
const buildPartialDentureCopyFragment = (prevItem: RemovableItem) => {
  const fragment: Record<string, unknown> = {};

  if (prevItem.teethToManufacture && prevItem.teethToManufacture.length > 0) {
    fragment.teethToManufacture = { set: prevItem.teethToManufacture };
  }

  const prevPd = prevItem.partialDenture;

  if (prevPd) {
    fragment.partialDenture = {
      create: {
        missingTeeth: { set: prevPd.missingTeeth ?? [] },
        palatalMetalBand: prevPd.palatalMetalBand ?? false,
        metalMesh: prevPd.metalMesh ?? false,
        lingualMetalBand: prevPd.lingualMetalBand ?? false,
        customTray: prevPd.customTray ?? false,
        ...(prevPd.hooks && prevPd.hooks.length > 0
          ? {
              hooks: {
                create: prevPd.hooks
                  .filter(h => h.hooksMaterial?.id)
                  .map(h => ({
                    hooksMaterial: { connect: { id: h.hooksMaterial!.id } },
                    hooksPosition: { set: h.hooksPosition ?? [] },
                  })),
              },
            }
          : {}),
      },
    };
  }

  return fragment;
};

// Same as buildPartialDentureCopyFragment but for the update shape
const buildPartialDentureCopyUpdateFragment = (prevItem: RemovableItem) => {
  const fragment: Record<string, unknown> = {};

  if (prevItem.teethToManufacture && prevItem.teethToManufacture.length > 0) {
    fragment.teethToManufacture = { set: prevItem.teethToManufacture };
  }

  const prevPd = prevItem.partialDenture;
  if (prevPd) {
    const hooksToCreate = (prevPd.hooks ?? [])
      .filter(h => h.hooksMaterial?.id)
      .map(h => ({
        hooksMaterial: { connect: { id: h.hooksMaterial!.id } },
        hooksPosition: { set: h.hooksPosition ?? [] },
      }));

    fragment.partialDenture = {
      upsert: {
        create: {
          missingTeeth: { set: prevPd.missingTeeth ?? [] },
          palatalMetalBand: prevPd.palatalMetalBand ?? false,
          metalMesh: prevPd.metalMesh ?? false,
          lingualMetalBand: prevPd.lingualMetalBand ?? false,
          customTray: prevPd.customTray ?? false,
          ...(hooksToCreate.length > 0
            ? { hooks: { create: hooksToCreate } }
            : {}),
        },
        update: {
          missingTeeth: { set: prevPd.missingTeeth ?? [] },
          palatalMetalBand: { set: prevPd.palatalMetalBand ?? false },
          metalMesh: { set: prevPd.metalMesh ?? false },
          lingualMetalBand: { set: prevPd.lingualMetalBand ?? false },
          customTray: { set: prevPd.customTray ?? false },
          // Replace hooks entirely: clears them, then create the new ones
          hooks: {
            deleteMany: {},
            ...(hooksToCreate.length > 0 ? { create: hooksToCreate } : {}),
          },
        },
      },
    };
  }

  return fragment;
};

// true when RDV2 item has not received teeth yet (and user hasn't edited them)
// so copying from previous item is safe and idempotent
const needsTeethCopy = (item: RemovableItem): boolean => {
  const hasManufacture = (item.teethToManufacture?.length ?? 0) > 0;
  const hasMissing = (item.partialDenture?.missingTeeth?.length ?? 0) > 0;

  return !hasManufacture && !hasMissing;
};

export const PartialDentureRdv2Choice = forwardRef<
  PartialDentureRdv2ChoiceRef,
  PartialDentureRdv2ChoiceProps
>(({ product, previousProduct, isEditMode = false, order }, ref) => {
  const previousRemovableItems = previousProduct?.removableItem ?? [];
  const previousRemovableItem = previousRemovableItems[0];

  const previousOrderType = previousRemovableItem?.partialDentureOrderType;
  const isAfterTryIn = previousOrderType === RemovableItemOrderType.TRY_IN;

  // In labs edit mode, use the current product's removableItem
  const existingRdv2Product = isEditMode
    ? undefined
    : order?.products?.find(
        (p: Product) =>
          p.productType === ItemType.PARTIAL_DENTURE &&
          (p.removableItem?.[0]?.imprintType === ImprintType.WAX_BITE_RDV2 ||
            p.removableItem?.[0]?.imprintType === ImprintType.TRY_IN_RDV2)
      );

  const newRemovableItem =
    existingRdv2Product?.removableItem?.[0] ?? product?.removableItem?.[0];

  const { control, watch, setValue } =
    useForm<PartialDentureRdv2ChoiceFormData>({
      defaultValues: {
        rdv2Choice: (newRemovableItem?.rdv2Choice as Rdv2ChoiceType) ?? null,
        returnMethod:
          (newRemovableItem?.returnMethod as Rdv2ReturnMethod) ?? null,
      },
    });

  const [updateOrder] = useMutation(UPDATE_ORDER, {
    refetchQueries: ["getOrderByUniqueAttribute"],
    awaitRefetchQueries: true,
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
    refetchQueries: ["getOrderByUniqueAttribute"],
    awaitRefetchQueries: true,
  });

  const rdv2Choice = watch("rdv2Choice");
  const returnMethod = watch("returnMethod");

  const rdv2ChoiceOptions = isAfterTryIn
    ? rdv2ChoiceOptionsAfterTryin
    : rdv2ChoiceOptionsAfterWaxBite;

  const getPartialDentureOrderType = (
    choice: Rdv2ChoiceType | null
  ): RemovableItemOrderType | null => {
    switch (choice) {
      case Rdv2ChoiceType.CONTINUE_TO_FINISH:
        return RemovableItemOrderType.FINAL;
      case Rdv2ChoiceType.REQUEST_TRY_IN:
        return RemovableItemOrderType.TRY_IN;
      case Rdv2ChoiceType.REQUEST_NEW_WAX_BITE:
        return RemovableItemOrderType.WAX_BITE;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (rdv2Choice === Rdv2ChoiceType.REQUEST_NEW_WAX_BITE) {
      setValue("returnMethod", null);
    }
  }, [rdv2Choice, setValue]);

  const saveData = async (): Promise<void> => {
    if (!rdv2Choice) return;

    const partialDentureOrderType = getPartialDentureOrderType(rdv2Choice);
    const imprintType = isAfterTryIn
      ? ImprintType.TRY_IN_RDV2
      : ImprintType.WAX_BITE_RDV2;

    try {
      let finalReturnMethod: Rdv2ReturnMethod | null = null;
      if (isAfterTryIn) {
        finalReturnMethod =
          rdv2Choice === Rdv2ChoiceType.CONTINUE_TO_FINISH
            ? Rdv2ReturnMethod.PICKUP
            : null;
      } else {
        finalReturnMethod =
          rdv2Choice !== Rdv2ChoiceType.REQUEST_NEW_WAX_BITE
            ? returnMethod
            : null;
      }

      if (isEditMode) {
        const currentRemovableItems = product?.removableItem ?? [];

        // 1) UPDATE the items already attached (if any)
        if (currentRemovableItems.length > 0) {
          await Promise.all(
            currentRemovableItems.map((item, index) => {
              const prevItem =
                previousRemovableItems[index] ?? previousRemovableItem;

              const updateData = {
                partialDentureOrderType: { set: partialDentureOrderType },
                rdv2Choice: { set: rdv2Choice },
                returnMethod: { set: finalReturnMethod },
                imprintType: { set: imprintType },
                rdvNumber: { set: (prevItem?.rdvNumber ?? 1) + 1 },
                ...(isAfterTryIn && prevItem?.itemMaterial?.id
                  ? {
                      itemMaterial: {
                        connect: { id: prevItem.itemMaterial.id },
                      },
                    }
                  : {}),
                ...(isAfterTryIn && prevItem?.teethShade?.id
                  ? { teethShade: { connect: { id: prevItem.teethShade.id } } }
                  : {}),
                ...(isAfterTryIn && prevItem?.gingivaShade?.id
                  ? {
                      gingivaShade: {
                        connect: { id: prevItem.gingivaShade.id },
                      },
                    }
                  : {}),
                ...(isAfterTryIn
                  ? {
                      isBimaxillarySeparated: {
                        set: prevItem?.isBimaxillarySeparated ?? false,
                      },
                    }
                  : {}),
                ...(prevItem?.teeth && (isAfterTryIn || needsTeethCopy(item))
                  ? { teeth: prevItem.teeth }
                  : {}),
                ...(prevItem && (isAfterTryIn || needsTeethCopy(item))
                  ? buildPartialDentureCopyUpdateFragment(prevItem)
                  : {}),
              };

              return updateRemovableItem({
                variables: { where: { id: item.id }, data: updateData },
              });
            })
          );
        }

        // 2) CREATE the missing items (e.g. second arch in a bimaxillary order)
        const missingPrevItems = previousRemovableItems.slice(
          currentRemovableItems.length
        );
        const allTeeth = previousRemovableItems.flatMap(
          item => item.teeth ?? []
        );
        const productUpdateData: Record<string, unknown> = {
          teeth: allTeeth,
          ...(isAfterTryIn && previousProduct?.itemPreferences
            ? { itemPreferences: previousProduct.itemPreferences }
            : {}),
          ...(missingPrevItems.length > 0
            ? {
                removableItem: {
                  create: missingPrevItems.map(prevItem => ({
                    itemType: ItemType.PARTIAL_DENTURE,
                    teeth: prevItem.teeth ?? [],
                    partialDentureOrderType,
                    imprintType,
                    rdvNumber: (prevItem.rdvNumber ?? 1) + 1,
                    rdv2Choice,
                    returnMethod: finalReturnMethod,
                    ...(isAfterTryIn && prevItem.itemMaterial?.id
                      ? {
                          itemMaterial: {
                            connect: { id: prevItem.itemMaterial.id },
                          },
                        }
                      : {}),
                    ...(isAfterTryIn && prevItem.teethShade?.id
                      ? {
                          teethShade: {
                            connect: { id: prevItem.teethShade.id },
                          },
                        }
                      : {}),
                    ...(isAfterTryIn && prevItem.gingivaShade?.id
                      ? {
                          gingivaShade: {
                            connect: { id: prevItem.gingivaShade.id },
                          },
                        }
                      : {}),
                    ...buildPartialDentureCopyFragment(prevItem),
                    isBimaxillarySeparated:
                      prevItem.isBimaxillarySeparated ?? false,
                  })),
                },
              }
            : {}),
        };

        await updateOrder({
          variables: {
            where: { id: product?.orderId },
            data: {
              products: {
                update: {
                  where: { id: product?.id },
                  data: productUpdateData,
                },
              },
            },
          },
        });
      } else if (
        existingRdv2Product &&
        newRemovableItem &&
        (previousRemovableItem?.rdvNumber ?? 1) === 1
      ) {
        // RDV2 product already exists — UPDATE
        const rdv2RemovableItems = existingRdv2Product.removableItem ?? [];
        await Promise.all(
          rdv2RemovableItems.map((item, index) => {
            const prevItem =
              previousRemovableItems[index] ?? previousRemovableItem;
            return updateRemovableItem({
              variables: {
                where: { id: item.id },
                data: {
                  partialDentureOrderType: { set: partialDentureOrderType },
                  rdv2Choice: { set: rdv2Choice },
                  returnMethod: { set: finalReturnMethod },
                  // Enrich with teeth from the previous (WAX_BITE/TRY_IN) item
                  // only if not already populated: idempotent on re-validation
                  ...(prevItem?.teeth && needsTeethCopy(item)
                    ? { teeth: prevItem.teeth }
                    : {}),
                  ...(prevItem && needsTeethCopy(item)
                    ? buildPartialDentureCopyUpdateFragment(prevItem)
                    : {}),
                },
              },
            });
          })
        );
      } else if (isAfterTryIn && previousRemovableItems.length > 0) {
        // After TRY_IN: Create new product with removableItems copied from previous
        const createItems = previousRemovableItems.map(prevItem => ({
          itemType: ItemType.PARTIAL_DENTURE,
          teeth: prevItem.teeth ?? [],
          partialDentureOrderType: partialDentureOrderType,
          imprintType: imprintType,
          rdvNumber: (prevItem.rdvNumber ?? 1) + 1,
          rdv2Choice: rdv2Choice,
          returnMethod:
            rdv2Choice === Rdv2ChoiceType.CONTINUE_TO_FINISH
              ? Rdv2ReturnMethod.PICKUP
              : null,
          ...(prevItem.itemMaterial?.id
            ? {
                itemMaterial: {
                  connect: { id: prevItem.itemMaterial.id },
                },
              }
            : {}),
          ...(prevItem.teethShade?.id
            ? {
                teethShade: {
                  connect: { id: prevItem.teethShade.id },
                },
              }
            : {}),
          ...(prevItem.gingivaShade?.id
            ? {
                gingivaShade: {
                  connect: { id: prevItem.gingivaShade.id },
                },
              }
            : {}),
          ...buildPartialDentureCopyFragment(prevItem),
          isBimaxillarySeparated: prevItem.isBimaxillarySeparated ?? false,
        }));

        // Merge all teeth from all removableItems
        const allTeeth = previousRemovableItems.flatMap(
          item => item.teeth ?? []
        );

        await updateOrder({
          variables: {
            where: { id: product?.orderId },
            data: {
              products: {
                create: [
                  {
                    productType: ItemType.PARTIAL_DENTURE,
                    teeth: allTeeth,
                    ...(previousProduct?.itemPreferences
                      ? { itemPreferences: previousProduct.itemPreferences }
                      : {}),
                    removableItem: {
                      create: createItems,
                    },
                  },
                ],
              },
            },
          },
        });
      } else if (previousRemovableItems.length > 0) {
        // After WAX_BITE: Create new product without copying characteristics
        const createItems = previousRemovableItems.map(prevItem => ({
          itemType: ItemType.PARTIAL_DENTURE,
          teeth: prevItem.teeth ?? [],
          partialDentureOrderType: partialDentureOrderType,
          imprintType: imprintType,
          rdvNumber: 2,
          rdv2Choice: rdv2Choice,
          returnMethod:
            rdv2Choice !== Rdv2ChoiceType.REQUEST_NEW_WAX_BITE
              ? returnMethod
              : null,
          ...buildPartialDentureCopyFragment(prevItem),
        }));

        const allTeeth = previousRemovableItems.flatMap(
          item => item.teeth ?? []
        );

        await updateOrder({
          variables: {
            where: { id: product?.orderId },
            data: {
              products: {
                create: [
                  {
                    productType: ItemType.PARTIAL_DENTURE,
                    teeth: allTeeth,
                    removableItem: {
                      create: createItems,
                    },
                  },
                ],
              },
            },
          },
        });
      }
    } catch (error) {
      console.error("[PartialDentureRdv2Choice] saveData: error", {
        error,
        isEditMode,
        isAfterTryIn,
        productId: product?.id,
        previousProductId: previousProduct?.id,
      });
    }
  };

  useImperativeHandle(ref, () => ({ saveData }));

  if (!previousRemovableItem && !newRemovableItem) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
        </Text>
      </div>
    );
  }

  const showReturnMethodOptions =
    !isAfterTryIn &&
    (rdv2Choice === Rdv2ChoiceType.CONTINUE_TO_FINISH ||
      rdv2Choice === Rdv2ChoiceType.REQUEST_TRY_IN);

  return (
    <Box>
      <Title order={3}>
        Comment souhaitez-vous procéder pour cette commande ?
      </Title>
      <Space h="md" />
      <Controller
        name="rdv2Choice"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <ButtonsSelect
            data={rdv2ChoiceOptions}
            value={field.value}
            onChange={value => field.onChange(value)}
            numberOfColumns={1}
            align="left"
          />
        )}
      />
      {showReturnMethodOptions && (
        <>
          <Space h="xl" />
          <Title order={4}>
            Comment souhaitez-vous retourner vos cires d'occlusion ?
          </Title>
          <Space h="md" />
          <Controller
            name="returnMethod"
            control={control}
            rules={{ required: showReturnMethodOptions }}
            render={({ field }) => (
              <ButtonsSelect
                data={returnMethodOptionsWaxBite}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={1}
                align="left"
              />
            )}
          />
        </>
      )}
      <Space h="xl" />
    </Box>
  );
});

PartialDentureRdv2Choice.displayName = "PartialDentureRdv2Choice";
